import RestClient from './RestClient';
import { Quote } from '../models';
import { Config } from '../config';
import { store } from '../redux';

class Quotes extends RestClient {
    constructor() {
        super({
            // rest config
            model: Quote,
            entryPoint: 'quotes',
            sortBy: 'slug',
            id_field: '_id',
            // redux config
            resource: 'quote',
            resources: 'quotes',
        });
    }

    toStep(_id: string, step: string, params?: AnyObj) {
        params = { _id, step };

        return this.PUT(`/${this.entryPoint}/${_id}/step`, params)
            .then(response => response.json())
            .then(json => {
                if (json.quote) {
                    json.quote = new Quote(json.quote);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    async downloadPdf(_id: string, filename: string, options: { signed?: boolean } = {}) {
        const subscriberId = store.getState()?.mercure?.subscriberId;
        const headers: AnyObj = {
            Accept: 'application/pdf',
        };

        if (subscriberId)
            headers['X-MERCURE-SUBSCRIBER'] = subscriberId;

        const query = options.signed ? '?signed=1' : '';
        const response = await fetch(`${Config.api_end_point}/${this.entryPoint}/${_id}/pdf${query}`, {
            method: 'GET',
            headers,
            credentials: 'include',
        });

        if (!response.ok)
            throw new Error('download_failed');

        const blob = await response.blob();
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    send(_id: string, payload: {
        to: string[] | string;
        cc?: string[] | string;
        subject?: string;
        personalMessage?: string;
        reminder?: boolean;
        signalUsers?: string[];
        signalGroups?: string[];
        trackedChange?: string | null;
    }) {
        return this.POST(`/${this.entryPoint}/${_id}/send`, payload)
            .then(response => response.json())
            .then(json => {
                if (json.quote) {
                    json.quote = new Quote(json.quote);
                    return json;
                }
                throw new Error(json.err || json.error || 'send_failed');
            });
    }

    sendEmail(_id: string, payload: {
        to: string[];
        cc?: string[];
        subject?: string;
        personalMessage?: string;
        reminder?: boolean;
    }) {
        return this.send(_id, payload);
    }

    getPublic(token: string) {
        return fetch(`${Config.api_end_point}/${this.entryPoint}/public/${token}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        }).then(async (response) => {
            const json = await response.json();
            if (!response.ok)
                throw new Error(json.err || 'not_found');
            return json;
        });
    }

    acceptPublic(token: string, payload: AnyObj) {
        return fetch(`${Config.api_end_point}/${this.entryPoint}/public/${token}/accept`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        }).then(async (response) => {
            const json = await response.json();
            if (!response.ok)
                throw new Error(json.err || 'accept_failed');
            return json;
        });
    }

    refusePublic(token: string) {
        return fetch(`${Config.api_end_point}/${this.entryPoint}/public/${token}/refuse`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
        }).then(async (response) => {
            const json = await response.json();
            if (!response.ok)
                throw new Error(json.err || 'refuse_failed');
            return json;
        });
    }
}

const QuotesApi = new Quotes();

export default QuotesApi;
